import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import { StyledCard, StyledHeader, StyledMeta, StyledDescription, StyledIcon, InformationWrapper, PrevievWrapper } from './style'
import { Button, Card, Grid, Icon, Message } from 'semantic-ui-react'
import notify from '~/components/Notify'
import { LoginPopupsService } from '~/api/services/loginPopups'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import Dialog from '~/components/Dialog'
import { googleDocsTypes, imageTypes, pdfType, videoTypes } from '~/Constants'
import { RequestCancellation } from '~/components/RequestCancellation'

type Props = {
  abortSignal: AbortSignal;
};

const LoginPopup: React.FunctionComponent<Props> = ({ abortSignal }) => {
  const [deletePopupId, setDeletePopupId] = useState(null)
  const [loadingFile, setLoadingFile] = useState(false)
  const navigate = useNavigate()
  const [fileSource, setFileSource] = useState(null)
  const [loginPopups, setLoginPopups] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const refIframe = useRef(null)

  useEffect(() => {
    fetchData(abortSignal);
  }, []);

  async function fetchData(signal: AbortSignal) {
    setIsBusy(true)
    try {
      const responseList = await LoginPopupsService.getLoginPopupsList(signal)
      setLoginPopups(responseList.data?.rows)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  async function fetchPreviewData(id) {
    setLoadingFile(true)
    setFileSource({ src: true })
    try {
      let response
      const showResponse = await LoginPopupsService.showLoginPopup(id)
      if (showResponse.data?.content) {
        response = await LoginPopupsService.getFileUrl(id)
      }
      setFileSource({
        src: response?.data || true,
        type: showResponse.data?.content?.split('.')[1],
        ...showResponse.data
      })
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingFile(false)
  }

  function renderList() {
    return loginPopups.map((popUp) => {
      const popUpEnabled = popUp.status === 1
      const { headerText } = popUp
      return <StyledCard raised key={popUp.id}>
        <Card.Content>
          <StyledHeader>{popUp.name}</StyledHeader>
          <StyledMeta>
            <Icon name='calendar outline' />
            <span>{dayjs(popUp.from).format('DD.MM.YYYY HH:mm')}</span>
            <Icon name='arrow right' />
            <span>{dayjs(popUp.to).format('DD.MM.YYYY HH:mm')}</span>
          </StyledMeta>
          <StyledDescription>
            {t('Popup enabled:')} <Icon color={popUpEnabled ? 'green' : 'red'} name={popUpEnabled ? 'checkmark' : 'close'} />
            <div dangerouslySetInnerHTML={{ __html: headerText }} />
          </StyledDescription>
        </Card.Content>
        <Card.Content extra>
          <Grid columns={3} centered divided>
            <Grid.Row>
              <Grid.Column textAlign='center'>
                <StyledIcon className='eye' onClick={() => fetchPreviewData(popUp.id)} />
              </Grid.Column>
              <Grid.Column textAlign='center'>
                <StyledIcon onClick={() => navigate(RouteTypes.SETTINGS_LOGIN_POPUP_EDIT.replace(':id', popUp.id))} className='edit' />
              </Grid.Column>
              <Grid.Column textAlign='center'>
                <StyledIcon className='trash' onClick={() => setDeletePopupId(popUp.id)} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card.Content>
      </StyledCard>
    })
  }

  async function handleDeletePopup() {
    setIsBusy(true)
    try {
      await LoginPopupsService.deleteLoginPopup(deletePopupId)
      setDeletePopupId(null)
      const responseList = await LoginPopupsService.getLoginPopupsList()
      notify(t('Successfully deleted login popup'), 'success')
      setLoginPopups(responseList.data?.rows)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function renderDeleteModal() {
    return <Dialog
      open={Boolean(deletePopupId)}
      handleClose={() => setDeletePopupId(null)}
      textHeader={t('Delete popup')}
      textContent={t('Do you really want to delete popup?')}
      textClose={t('Close')}
      isLoading={isBusy}
      textSubmit={t('Delete')}
      handleSubmit={() => handleDeletePopup()}
      type={'abort'}
    />
  }

  function renderNoResultMessage() {
    return !isBusy && <Message info style={{ margin: '20px auto', textAlign: 'center', maxHeight: 70 }}>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no login popups to display.')}</p>
    </Message>
  }

  function renderPreviewInformation() {
    return <>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Created at:')}</div>
        <div className='value'>{dayjs(fileSource?.createdAt).format('DD.MM.YYYY HH:mm')}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('File type:')}</div>
        <div className='value'>{fileSource?.type}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Date from:')}</div>
        <div className='value'>{dayjs(fileSource?.from).format('DD.MM.YYYY HH:mm')}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Date to:')}</div>
        <div className='value'>{dayjs(fileSource?.to).format('DD.MM.YYYY HH:mm')}</div>
      </div>
    </>
  }

  function handleRefreshClick() {
    if (refIframe.current) {
      refIframe.current.setAttribute('src', '')
      //@ts-ignore
      const encodedUrl = encodeURIComponent(fileSource?.src);
      // Construct the embedded URL
      const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
      refIframe.current.setAttribute('src', embeddedUrl)
    }
  }

  const renderModalPreviewFile = () => {
    const title = fileSource?.title
    const googleDocsType = googleDocsTypes.find(type => type === fileSource?.type)
    return (
      <Dialog
        open={Boolean(fileSource?.src)}
        heightDialog={pdfType === fileSource?.type ? null : 'auto'}
        handleClose={() => setFileSource(null)}
        textContent={<div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div style={{ margin: 20 }} dangerouslySetInnerHTML={{ __html: fileSource?.headerText }} />
          {fileSource?.type && <>
            <InformationWrapper>
              {renderPreviewInformation()}
              <Button type={'button'}
                onClick={() => handleRefreshClick()}
                basic
                icon={'refresh'}
                content={t('Refresh')} />
            </InformationWrapper>
            <PrevievWrapper>
              {(imageTypes.find(imageType => imageType === fileSource?.type)) && <img src={fileSource?.src as string} alt='Preview' />}
            </PrevievWrapper>
            <PrevievWrapper>
              {videoTypes.find(videoType => videoType === fileSource?.type) && <video src={fileSource?.src as string} controls />}
            </PrevievWrapper>
            {googleDocsType && fileSource?.type && (
              <iframe
                ref={refIframe}
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileSource?.src)}&embedded=true`}
                width='100%'
                height='80%'
              />
            )}
          </>
          }
          <div style={{ margin: 20 }} dangerouslySetInnerHTML={{ __html: fileSource?.footerText }} />
        </div>}
        textHeader={t('Preview: {{title}}', { title })}
        textClose={t('Close')}
        type={'preview'}
        isLoading={loadingFile}
      />
    );
  };

  return <Layout
    title={t('Login popups')}
    isBusy={isBusy}
    segmented
    actionName={t('Create login popup')}
    action={RouteTypes.SETTINGS_LOGIN_POPUP_CREATE}
  >
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
      {loginPopups.length === 0 ? renderNoResultMessage() : renderList()}
    </div>
    {renderModalPreviewFile()}
    {renderDeleteModal()}
  </Layout>
}
export default RequestCancellation(LoginPopup)
